import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../../environments/environment';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { firstValueFrom, take } from 'rxjs';
import { MatDivider } from '@angular/material/divider';
import { MatInput } from '@angular/material/input';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatTab, MatTabChangeEvent, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { AuthState } from '../../../core/authentication/auth.state';
import { MatCard, MatCardContent } from '@angular/material/card';
import { PixlEditorComponent } from '@wdidev/pixl-editor';
import { SequenceEntity } from '../../../sequence/sequence.entity';
import { ExplanationContentEntity } from '../../../explanation/content/explanation-content-entity';
import { ExplanationEntity } from '../../../explanation/explanation-entity';
import { TestEntity } from '../../../test/test.entity';
import { LearningSectionEntity } from '../../learning-section-entity';
import {
  ExplanationContentEntityService
} from '../../../explanation/content/services/explanation-content.entity.service';
import { TestEntityService } from '../../../test/services/test.entity.service';
import { SequenceEntityService } from '../../../sequence/services/sequence.entity.service';
import { ExplanationEntityService } from '../../../explanation/services/explanation.entity.service';
import { LearningSectionEntityService } from '../../services/learning-section.entity.service';
import { toggleContenteditable, toggleReadOnlyMathEditorInput } from '../../../core/util';
import { ExplanationContentTabLoader } from '../../../explanation/content/utils/explanation-content-tab.loader';
import { SequenceTabLoader } from '../../../sequence/utils/sequence-tab.loader';
import { ExplanationTabLoader } from '../../../explanation/utils/explanation-tab.loader';
import { TestTabLoader } from '../../../test/utils/test-tab.loader';
import { LearningSectionTabLoader } from '../../utils/learning-section-tab.loader';
import {
  AbstractEntityTabLoader
} from '../../../utils/component/abstract-entity-tab-loader/abstract-entity-tab.loader';
import { messageTypes, WidgetMessageService } from '../../../core/services/widget-message.service';
import { TableLoadingSpinnerComponent } from '../../../utils/component/table-loading-spinner/table-loading-spinner';
import { MatChip, MatChipListbox } from '@angular/material/chips';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonBarItemData } from '../../learning-section-entity';
import { EntityStateModel } from '../../../core/state/entity.state';
import { ConsumerEntity } from '../../../consumer/consumer.entity';
import { ConsumerEntityService } from '../../../consumer/services/consumer.entity.service';

@Component({
  selector: 'pixl-form-dialog-button-bar-item',
  imports: [
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatDialogModule,
    AsyncPipe,
    CommonModule,
    MatDivider,
    ReactiveFormsModule,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    MatError,
    MatInput,
    FormsModule,
    MatIconButton,
    MatCard,
    PixlEditorComponent,
    TableLoadingSpinnerComponent,
    MatCardContent,
    MatChipListbox,
    MatChip,
    TranslocoModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './form-dialog-button-bar-item.component.html',
  styleUrl: './form-dialog-button-bar-item.component.scss'
})
export class FormDialogButtonBarItemComponent implements OnInit, AfterViewInit {
  private cdr = inject(ChangeDetectorRef);
  private dialogRef = inject(MatDialogRef);
  private widgetService = inject(WidgetMessageService);
  private destroyRef = inject(DestroyRef);

  private store = inject(Store);
  private fb = inject(FormBuilder);

  private explanationContentService = inject(ExplanationContentEntityService);
  private testService = inject(TestEntityService);
  private sequenceService = inject(SequenceEntityService);
  private explanationService = inject(ExplanationEntityService);
  private learningSectionService = inject(LearningSectionEntityService);
  private consumerService = inject(ConsumerEntityService);
  private translocoService = inject(TranslocoService);

  public data = inject<ButtonBarItemData>(MAT_DIALOG_DATA);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public missingEntitySelection: any = null;

  public assetsUrl: string = environment.apiUrl + '/assets';
  public mediaUrl: string = environment.assetsHost + '/';

  auth$ = this.store.select(AuthState.getToken);
  public authToken: string | null = '';

  public subSectionsActive = false;

  public formGroup = this.fb.group({
    label: [this.data.label || '', Validators.required],
    content: [null as { id: string, type: string } | null]
  });

  explanationContentLoader = new ExplanationContentTabLoader(
    this.store,
    this.explanationContentService
  );

  public explanationContents: ExplanationContentEntity[] = [];
  public explanationContentName = new FormControl('', [Validators.required]);
  public explanationContentHtml = '';

  sequenceLoader = new SequenceTabLoader(
    this.store,
    this.sequenceService
  );

  public sequences: SequenceEntity[] = [];

  explanationLoader = new ExplanationTabLoader(
    this.store,
    this.explanationService
  );

  public explanations: ExplanationEntity[] = [];

  testLoader = new TestTabLoader(
    this.store,
    this.testService
  );

  public tests: TestEntity[] = [];

  learningSectionLoader = new LearningSectionTabLoader(
    this.store,
    this.learningSectionService
  );

  public learningSections: LearningSectionEntity[] = [];

  editor = viewChild(PixlEditorComponent);

  selectedContent: SequenceEntity | ExplanationEntity | ExplanationContentEntity | TestEntity | LearningSectionEntity | null = null;
  contentRef?: { id: string, type: string };

  public originalSavedName = '';
  public searchTerm = '';
  public selectedTabLabel: string | null = null;
  public lockTabs = false;
  public activeTabIndex = 0;

  ngOnInit(): void {
    this.resolveSubSectionsActive();
    this.loadRelationData();

    this.auth$.pipe(take(1)).subscribe(token => {
      this.authToken = token;
    });

    // If existing data has content, restore the selection
    if (this.data.contentType && this.data.contentId) {
      this.contentRef = { id: this.data.contentId, type: this.data.contentType };
    }

    // Set active tab based on contentRef
    this.initActiveTab();
  }

  async ngAfterViewInit(): Promise<void> {
    await this.explanationContentLoader.loadInitial();
    await this.sequenceLoader.loadInitial();
    await this.explanationLoader.loadInitial();
    await this.testLoader.loadInitial();
    await this.learningSectionLoader.loadInitial();

    // Once loaders are ready, select existing content if present
    if (this.contentRef) {
      setTimeout(() => {
        this.selectContentByType(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.contentRef!.type as any,
          this.contentRef!.id
        );
        this.cdr.markForCheck();
      }, 0);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  compareContentById(a: any, b: any): boolean {
    if (a === b) return true;
    if (!a || !b) return false;

    const idA = typeof a === 'string' ? a : a.id;
    const idB = typeof b === 'string' ? b : b.id;

    return idA === idB;
  }

  private selectContentByType(
    type: 'explanation-contents' | 'sequences' | 'explanations' | 'tests' | 'learning-sections',
    id: string
  ): void {
    switch (type) {
      case 'explanation-contents':
        this.setEditorContent();
        return;

      case 'sequences':
        this.selectFromLoader(this.sequenceLoader, id);
        return;

      case 'explanations':
        this.selectFromLoader(this.explanationLoader, id);
        return;

      case 'tests':
        this.selectFromLoader(this.testLoader, id);
        return;

      case 'learning-sections':
        this.selectFromLoader(this.learningSectionLoader, id);
        return;
    }
  }

  private async selectFromLoader<T extends { id: string }>(
    loader: AbstractEntityTabLoader<T>,
    id: string
  ): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const loaderAny = loader as any;
    const list = await firstValueFrom(loader.entities$.pipe(take(1)));
    let match = list?.find((e: T) => e.id === id) ?? null;

    if (!match) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let service: any;
      if (loaderAny === this.sequenceLoader) service = this.sequenceService;
      else if (loaderAny === this.testLoader) service = this.testService;
      else if (loaderAny === this.explanationLoader) service = this.explanationService;
      else if (loaderAny === this.learningSectionLoader) service = this.learningSectionService;

      if (service) {
        await service.ensureSingleLoaded(id);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const state = this.store.selectSnapshot(service.stateClass) as any;
        match = state.entities[id] ?? null;

        if (match && !list.some(e => e.id === id)) {
          this.missingEntitySelection = match;
        }
      }
    }
    else {
      this.missingEntitySelection = null;
    }

    if (match && (!this.originalSavedName || this.originalSavedName === '')) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.originalSavedName = (match as any).name || '';
    }

    this.selectedContent = match;
    this.cdr.markForCheck();
  }

  public loadRelationData(): void {
    this.explanationContentLoader.entities$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => {
      this.explanationContents = list;
      this.cdr.markForCheck();
    });

    this.sequenceLoader.entities$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => {
      this.sequences = list;
      this.cdr.markForCheck();
    });

    this.explanationLoader.entities$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => {
      this.explanations = list;
      this.cdr.markForCheck();
    });

    this.testLoader.entities$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => {
      this.tests = list;
      this.cdr.markForCheck();
    });

    this.learningSectionLoader.entities$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => {
      this.learningSections = list;
      this.cdr.markForCheck();
    });
  }

  public getContentTypeLabel(type: string | undefined): string {
    const mapping: Record<string, string> = {
      'explanation-contents': this.translocoService.translate('contentType.editor'),
      'sequences': this.translocoService.translate('contentType.sequence'),
      'explanations': this.translocoService.translate('contentType.explanation'),
      'tests': this.translocoService.translate('contentType.test'),
      'learning-sections': this.translocoService.translate('contentType.learningSection'),
    };
    return type ? mapping[type] || type : '';
  }

  private getTypeForTab(tabLabel: string | null): string | null {
    const mapping: Record<string, string> = {
      editor: 'explanation-contents',
      sequence: 'sequences',
      explanation: 'explanations',
      test: 'tests',
      learningSection: 'learning-sections',
    };

    return tabLabel ? mapping[tabLabel] ?? null : null;
  }

  private initActiveTab(): void {
    const type = this.contentRef?.type;
    const labels = this.getRenderedTabLabels();

    let targetLabel = 'editor';
    if (type === 'sequences') targetLabel = 'sequence';
    if (type === 'explanations') targetLabel = 'explanation';
    if (type === 'tests') targetLabel = 'test';
    if (type === 'learning-sections') targetLabel = 'learningSection';

    this.selectedTabLabel = targetLabel;
    const index = labels.indexOf(targetLabel);
    this.activeTabIndex = index >= 0 ? index : 0;
    this.lockTabs = !!this.contentRef;
  }

  async onTabChange(event: MatTabChangeEvent): Promise<void> {
    this.activeTabIndex = event.index;

    const tabLabels = this.getRenderedTabLabels();
    this.selectedTabLabel = tabLabels[event.index] ?? null;

    const loader = this.getActiveLoader();
    if (loader) {
      await loader.loadInitial();
    }
  }

  getRenderedTabLabels(): string[] {
    return ['editor', 'sequence', 'test', 'learningSection'];
  }

  isTabLocked(tabLabel: string): boolean {
    return this.lockTabs && tabLabel !== this.selectedTabLabel;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getActiveLoader(): AbstractEntityTabLoader<any> {
    return [
      this.explanationContentLoader,
      this.sequenceLoader,
      this.testLoader,
      this.learningSectionLoader,
    ][this.activeTabIndex];
  }

  async onSearchChange(): Promise<void> {
    await this.getActiveLoader().search(this.searchTerm);
  }

  async onSelectOpened(
    opened: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    loader: AbstractEntityTabLoader<any>,
    select?: MatSelect
  ): Promise<void> {
    if (!opened || !select) return;

    await new Promise(resolve => setTimeout(resolve, 0));

    const panelEl = select.panel?.nativeElement;
    if (!panelEl) return;

    const scrollHandler = async () => {
      const threshold = 50;
      if (panelEl.scrollHeight - panelEl.scrollTop - panelEl.clientHeight < threshold) {
        await loader.loadMore();
      }
    };

    panelEl.addEventListener('scroll', scrollHandler);

    const closeSub = select._closedStream.subscribe(() => {
      panelEl.removeEventListener('scroll', scrollHandler);
      closeSub.unsubscribe();
    });
  }

  async setEditorContent(): Promise<void> {
    if (!this.contentRef?.id) return;

    await this.explanationContentService.ensureSingleLoaded(this.contentRef.id, {});

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const state = this.store.selectSnapshot(this.explanationContentService.stateClass) as any;
    const loadedContent = state.entities[this.contentRef.id];

    if (loadedContent) {
      const list = await firstValueFrom(this.explanationContentLoader.entities$.pipe(take(1)));
      const isInList = list?.some(e => e.id === loadedContent.id);

      if (!isInList) {
        this.missingEntitySelection = loadedContent;
      }
      else {
        this.missingEntitySelection = null;
      }

      this.selectedContent = loadedContent;
      this.explanationContentName.setValue(loadedContent.name || '');

      if (!this.originalSavedName) {
        this.originalSavedName = loadedContent.name || '';
      }

      let html = loadedContent.content || '';
      html = toggleReadOnlyMathEditorInput(html, 'remove');
      html = toggleContenteditable(html, 'set');
      this.explanationContentHtml = html;

      this.cdr.markForCheck();
    }
  }

  async onEditorSelectClosed(): Promise<void> {
    if (!this.isExplanationContent(this.selectedContent)) return;

    this.explanationContentName.setValue(this.selectedContent.name || '');

    let html = this.selectedContent.content || '';
    html = toggleReadOnlyMathEditorInput(html, 'remove');
    html = toggleContenteditable(html, 'set');

    this.explanationContentHtml = html;
  }

  private isExplanationContent(
    value: SequenceEntity | ExplanationEntity | ExplanationContentEntity | TestEntity | LearningSectionEntity | null
  ): value is ExplanationContentEntity {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return !!value && typeof (value as any).content === 'string';
  }

  checkNewEditorContent(): boolean {
    return !this.isExplanationContent(this.selectedContent);
  }

  private finalizeEditorHtml(): string {
    if (!this.explanationContentHtml?.trim()) {
      return '<p></p>';
    }

    let html = toggleReadOnlyMathEditorInput(this.explanationContentHtml, 'set');
    html = toggleContenteditable(html, 'remove');
    return html;
  }

  public async onCopy(): Promise<void> {
    const originalName = this.explanationContentName.value?.trim() ?? this.translocoService.translate('buttonBarItem.defaultContentName');
    const copyName = `${originalName} (${this.translocoService.translate('buttonBarItem.copySuffix')})`;

    const html = this.finalizeEditorHtml();

    const newContentPayload: Partial<ExplanationContentEntity> = {
      name: copyName,
      content: html,
      version: 1
    };

    try {
      const created = await this.explanationContentService.create(newContentPayload);

      this.selectedContent = created;
      this.explanationContentName.setValue(created.name || copyName);

      let createdHtml = created.content || '';
      createdHtml = toggleReadOnlyMathEditorInput(createdHtml, 'remove');
      createdHtml = toggleContenteditable(createdHtml, 'set');
      this.explanationContentHtml = createdHtml;

      this.cdr.markForCheck();
      this.showMessage(this.translocoService.translate('buttonBarItem.copySuccess'));
    }
    catch (err) {
      console.error('Error creating copy:', err);
      this.showMessage(this.translocoService.translate('buttonBarItem.copyError'), 'mtError');
    }
  }

  async onSave(): Promise<void> {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const formData = this.formGroup.value;
    let contentRelation: { id: string, type: string } | undefined = undefined;
    const type = this.getTypeForTab(this.selectedTabLabel);

    if (this.selectedTabLabel === 'editor') {
      const content = await this.resolveEditorContent();
      if (!content) return;
      contentRelation = { id: content.id, type: 'explanation-contents' };
    }
    else if (this.selectedContent && type) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      contentRelation = { id: (this.selectedContent as any).id, type: type };
    }

    const result: ButtonBarItemData = {
      label: formData.label || '',
      contentType: contentRelation?.type ?? null,
      contentId: contentRelation?.id ?? null,
    };

    this.dialogRef.close(result);
  }

  private showMessage(message: string, messageTye: messageTypes = 'mtSuccess'): void {
    this.widgetService.setMessage({
      message,
      messageTye
    });
  }

  public checkForErrors(formGroup: AbstractControl): string {
    if (formGroup.hasError('required')) {
      return this.translocoService.translate('validation.required');
    }

    return '';
  }

  private async resolveEditorContent(): Promise<ExplanationContentEntity | null> {
    if (this.explanationContentName.invalid) return null;

    const html = this.finalizeEditorHtml();

    try {
      if (this.checkNewEditorContent()) {
        const newContent: Partial<ExplanationContentEntity> = {
          name: this.explanationContentName.value!,
          content: html,
          version: 1,
        };

        const created = await this.explanationContentService.create(newContent);
        this.selectedContent = created;
        return created;
      }

      if (this.isExplanationContent(this.selectedContent)) {
        const payload = Object.assign(
          new ExplanationContentEntity(),
          this.selectedContent,
          {
            name: this.explanationContentName.value!,
            content: html,
            version: this.selectedContent.version ?? 1,
          }
        );

        const updated = await firstValueFrom(
          this.explanationContentService.update(payload)
        );

        this.selectedContent = updated;
        return updated;
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    catch (error: any) {
      const errors = error?.error?.errors || error?.errors;
      if (Array.isArray(errors)) {
        const nameUniqueError = errors.find(e =>
          e.source?.pointer?.includes('name') && e.detail?.includes('unique')
        );

        if (nameUniqueError) {
          this.explanationContentName.setErrors({ unique: true });
          this.explanationContentName.markAsTouched();
          this.showMessage(this.translocoService.translate('buttonBarItem.nameAlreadyExists'), 'mtError');
        }
        else {
          this.showMessage(this.translocoService.translate('buttonBarItem.validationError'), 'mtError');
        }
      }
      return null;
    }

    return null;
  }

  private resolveSubSectionsActive(): void {
    const consumerId = this.data.consumerId;
    if (!consumerId) return;

    const consumerState = this.store.selectSnapshot(
      this.consumerService.stateClass,
    ) as EntityStateModel<ConsumerEntity>;
    const consumer = consumerState.entities[consumerId];

    if (!consumer?.consumerData) return;

    this.subSectionsActive = consumer.consumerData.sub_sections_active === true;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
